if (!customElements.get('td-stats-hero')) {
  class TDStatsHero extends HTMLElement {
    static selectors = {
      counter: '[data-td-stats-counter]',
      media: '[data-td-stats-media]',
    };

    static classes = {
      mediaPinned: 'td-stats-hero__media--pinned',
    };

    // Entry sweep smoothing (ms); desktop pin uses fixed positioning.
    static scrollSmoothingTau = 45;

    constructor() {
      super();
      this.handleMediaIntersection = this.handleMediaIntersection.bind(this);
      this.handleCounterIntersection = this.handleCounterIntersection.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleMediaLoad = this.handleMediaLoad.bind(this);
      this.renderMediaFrame = this.renderMediaFrame.bind(this);
      this.handleCounterSectionIntersection = this.handleCounterSectionIntersection.bind(this);
    }

    connectedCallback() {
      this.media = this.querySelector(TDStatsHero.selectors.media);
      this.mediaImage = this.media?.querySelector('img') || null;
      this.counters = Array.from(this.querySelectorAll(TDStatsHero.selectors.counter)).map((el) => ({
        el,
        target: parseFloat(el.dataset.tdTarget || '0'),
        unit: el.dataset.tdUnit || '',
        decimals: parseInt(el.dataset.tdDecimals || '0', 10) || 0,
        hasAnimated: false,
        frameId: null,
      }));

      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.squeezeQuery = window.matchMedia('(min-width: 990px)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mediaObserver = null;
      this.counterObserver = null;
      this.counterSectionObserver = null;
      this.layoutObserver = null;
      this.motionMetrics = null;
      this.renderFrame = null;
      this.currentTransform = null;
      this.isTrackingMedia = false;
      // Mobile: CSS scroll timeline when supported; JS fallback for desktop pin.
      this.supportsScrollTimeline =
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('animation-timeline: view()');
      // Cache width only — mobile URL-bar height resizes must not recalibrate mid-scroll.
      this.lastViewportWidth = window.innerWidth;
      this.viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;

      // Reduced motion: settled transform + final counter values only.
      if (this.reduceMotionQuery.matches) {
        this.mediaImage?.addEventListener('load', this.handleMediaLoad);
        this.updateMotionMetrics();
        this.setFinalCounters();
        return;
      }

      this.setupCounterObserver();
      this.setupCounterSectionObserver();
      this.setupLayoutObserver();

      if (this.media) {
        this.mediaImage?.addEventListener('load', this.handleMediaLoad);
        window.addEventListener('resize', this.handleResize, { passive: true });
        this.updateMotionMetrics();
        this.applyMediaMotionMode();
      }

      this.bindMediaQueryListeners(true);
    }

    disconnectedCallback() {
      this.stopMediaTracking();
      this.bindMediaQueryListeners(false);
      window.removeEventListener('resize', this.handleResize);
      this.mediaImage?.removeEventListener('load', this.handleMediaLoad);
      this.media?.classList.remove(TDStatsHero.classes.mediaPinned);

      if (this.mediaObserver) {
        this.mediaObserver.disconnect();
        this.mediaObserver = null;
      }

      if (this.counterObserver) {
        this.counterObserver.disconnect();
        this.counterObserver = null;
      }

      if (this.counterSectionObserver) {
        this.counterSectionObserver.disconnect();
        this.counterSectionObserver = null;
      }

      if (this.layoutObserver) {
        this.layoutObserver.disconnect();
        this.layoutObserver = null;
      }

      if (this.renderFrame) {
        cancelAnimationFrame(this.renderFrame);
        this.renderFrame = null;
      }

      this.counters?.forEach((counter) => {
        if (counter.frameId) {
          cancelAnimationFrame(counter.frameId);
          counter.frameId = null;
        }
      });
    }

    bindMediaQueryListeners(shouldBind) {
      const method = shouldBind ? 'addEventListener' : 'removeEventListener';

      [this.desktopQuery, this.squeezeQuery].forEach((query) => {
        query[method]('change', this.handleBreakpointChange);
      });
    }

    setupCounterObserver() {
      if (!this.counters.length) {
        return;
      }

      if (this.counterObserver) {
        this.counterObserver.disconnect();
      }

      this.counterObserver = new IntersectionObserver(this.handleCounterIntersection, {
        root: this.getIntersectionRoot(),
        threshold: 0.45,
      });

      this.counters.forEach((counter) => {
        if (counter.hasAnimated) {
          return;
        }

        counter.el.textContent = `${this.formatCounterValue(0, counter.decimals)}${counter.unit}`;
        this.counterObserver.observe(counter.el);
      });
    }

    setupCounterSectionObserver() {
      if (!this.counters.length) {
        return;
      }

      this.counterSectionObserver?.disconnect();
      this.counterSectionObserver = new IntersectionObserver(this.handleCounterSectionIntersection, {
        root: this.getIntersectionRoot(),
        threshold: 0,
      });
      this.counterSectionObserver.observe(this);
    }

    handleCounterSectionIntersection(entries) {
      const entry = entries[entries.length - 1];
      if (!entry?.isIntersecting) {
        this.resetCounters();
      }
    }

    resetCounters() {
      this.counters.forEach((counter) => {
        if (counter.frameId) {
          cancelAnimationFrame(counter.frameId);
          counter.frameId = null;
        }

        counter.hasAnimated = false;
        counter.el.textContent = `${this.formatCounterValue(0, counter.decimals)}${counter.unit}`;
      });

      this.setupCounterObserver();
    }

    setupLayoutObserver() {
      if (!this.media || typeof ResizeObserver === 'undefined') {
        return;
      }

      this.layoutObserver?.disconnect();
      this.layoutObserver = new ResizeObserver(() => {
        this.updateMotionMetrics();
        this.renderOnce();
      });
      this.layoutObserver.observe(this);
    }

    handleCounterIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.45) {
          return;
        }

        const counter = this.counters.find((item) => item.el === entry.target);
        if (!counter || counter.hasAnimated || Number.isNaN(counter.target)) {
          return;
        }

        counter.hasAnimated = true;
        this.counterObserver?.unobserve(counter.el);
        this.animateCounter(counter);
      });
    }

    animateCounter(counter) {
      const duration = 1100;
      const startTime = performance.now();

      const step = (timestamp) => {
        if (!this.isConnected) {
          return;
        }

        const elapsed = timestamp - startTime;
        const progress = this.clamp(elapsed / duration, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = counter.target * eased;

        counter.el.textContent = `${this.formatCounterValue(value, counter.decimals)}${counter.unit}`;

        if (progress < 1) {
          counter.frameId = requestAnimationFrame(step);
        } else {
          counter.el.textContent = `${this.formatCounterValue(counter.target, counter.decimals)}${counter.unit}`;
          counter.frameId = null;
        }
      };

      counter.frameId = requestAnimationFrame(step);
    }

    setFinalCounters() {
      this.counters.forEach((counter) => {
        if (Number.isNaN(counter.target)) {
          return;
        }

        counter.el.textContent = `${this.formatCounterValue(counter.target, counter.decimals)}${counter.unit}`;
      });
    }

    refreshMediaObserver() {
      if (!this.media) {
        return;
      }

      if (this.mediaObserver) {
        this.mediaObserver.disconnect();
      }

      this.mediaObserver = new IntersectionObserver(this.handleMediaIntersection, {
        root: this.getIntersectionRoot(),
        rootMargin: '35% 0px 35% 0px',
        threshold: 0,
      });

      this.mediaObserver.observe(this);
    }

    // Mobile + scroll-timeline support: CSS owns the transform.
    prefersCssTimeline() {
      return this.supportsScrollTimeline && !this.desktopQuery.matches;
    }

    applyMediaMotionMode() {
      if (!this.media) {
        return;
      }

      if (this.prefersCssTimeline()) {
        // CSS drives transform; JS only sets path custom properties.
        if (this.mediaObserver) {
          this.mediaObserver.disconnect();
          this.mediaObserver = null;
        }
        this.stopMediaTracking();
        this.media.classList.remove(TDStatsHero.classes.mediaPinned);
        return;
      }

      this.refreshMediaObserver();
      this.renderOnce();
    }

    handleMediaIntersection(entries) {
      const entry = entries[entries.length - 1];
      if (!entry) {
        return;
      }

      if (entry.isIntersecting) {
        this.startMediaTracking();
      } else {
        this.stopMediaTracking();
      }
    }

    startMediaTracking() {
      if (this.isTrackingMedia) {
        return;
      }

      this.isTrackingMedia = true;
      this.renderOnce();
      this.startRenderLoop();
    }

    stopMediaTracking() {
      if (!this.isTrackingMedia) {
        return;
      }

      this.isTrackingMedia = false;
      this.stopRenderLoop();
      this.renderOnce();
    }

    handleResize() {
      const width = window.innerWidth;

      // Mobile: skip height-only resizes (URL bar); width/orientation still refresh.
      if (!this.desktopQuery.matches && width === this.lastViewportWidth) {
        return;
      }

      this.lastViewportWidth = width;
      this.updateMotionMetrics();
      this.renderOnce();
    }

    handleBreakpointChange() {
      this.lastViewportWidth = window.innerWidth;
      this.updateMotionMetrics();
      this.setupCounterObserver();
      this.applyMediaMotionMode();
    }

    handleMediaLoad() {
      this.updateMotionMetrics();
      this.renderOnce();
    }

    updateMotionMetrics() {
      if (!this.media) {
        return;
      }

      this.viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;

      const firstItem = this.querySelector('.td-stats-hero__item');
      const copy = firstItem?.querySelector('.td-stats-hero__copy');
      const values = firstItem?.querySelector('.td-stats-hero__values');
      const inner = this.querySelector('.td-stats-hero__inner');
      const sectionRect = this.getBoundingClientRect();
      // Horizontal frame is __inner (shared with page-width column); sectionRect for vertical math.
      const containerRect = inner ? inner.getBoundingClientRect() : sectionRect;
      const styles = window.getComputedStyle(this);
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      const mediaWidth = this.media.offsetWidth || this.mediaImage?.offsetWidth || 0;
      const mediaHeight = this.media.offsetHeight || this.mediaImage?.offsetHeight || 0;

      if (!mediaWidth || !mediaHeight || !containerRect.width || !sectionRect.height) {
        return;
      }

      const isDesktop = this.desktopQuery.matches;
      let endLeft = 0;
      // Entry tilt; 0deg = upright resting pose.
      const startRotate = isDesktop ? 66 : 85;

      if (isDesktop && copy && values) {
        const copyRect = copy.getBoundingClientRect();
        const valuesRect = values.getBoundingClientRect();
        const gapCenter = (copyRect.right + valuesRect.left) / 2;
        endLeft = gapCenter - containerRect.left - mediaWidth / 2;
      } else if (isDesktop) {
        const viewportCenter = window.innerWidth / 2;
        endLeft = viewportCenter - containerRect.left - mediaWidth / 2;
      } else if (values) {
        endLeft = mediaWidth * -0.62;
      } else {
        endLeft = containerRect.width * 0.15;
      }

      const minLeft = isDesktop ? 0 : mediaWidth * -0.62;
      endLeft = this.clamp(endLeft, minLeft, Math.max(minLeft, containerRect.width - mediaWidth));

      const endBottom = paddingBottom;
      // Start off-screen right, accounting for rotated bounding box + shadow.
      const startRotateRad = (startRotate * Math.PI) / 180;
      const rotatedHalfWidth = (mediaWidth * Math.cos(startRotateRad) + mediaHeight * Math.sin(startRotateRad)) / 2;
      const startLeft = window.innerWidth - containerRect.left + rotatedHalfWidth - mediaWidth / 2 + mediaWidth * 0.5;

      if (isDesktop) {
        this.motionMetrics = {
          mode: 'desktop',
          startX: startLeft - endLeft,
          startRotate,
          endBottom,
          pinMargin: 0.08, /* vh above viewport bottom while pinned */
          sweepWindow: 0.45, /* horizontal sweep + straighten within this progress fraction */
          arcLift: this.viewportHeight * 0.05, /* vertical lob during sweep */
        };
      } else {
        const viewportHeight = this.viewportHeight;
        const finalTop = sectionRect.height - endBottom - mediaHeight;
        const startTop = -mediaHeight - viewportHeight * 0.06;
        const startX = startLeft - endLeft;
        const startY = startTop - finalTop;

        this.motionMetrics = {
          mode: 'mobile',
          startX,
          startY,
          startRotate,
        };

        // CSS keyframes path; --td-stats-range-end maps JS progress onto cover range.
        const coverTravel = sectionRect.height + viewportHeight * 0.14;
        const coverTotal = viewportHeight + sectionRect.height;
        this.media.style.setProperty('--td-stats-mstart-x', `${startX.toFixed(2)}px`);
        this.media.style.setProperty('--td-stats-mstart-y', `${startY.toFixed(2)}px`);
        this.media.style.setProperty('--td-stats-mstart-rotate', `${startRotate}deg`);
        this.media.style.setProperty(
          '--td-stats-range-end',
          `${this.clamp((coverTravel / coverTotal) * 100, 0, 100).toFixed(2)}%`,
        );
      }

      // Resting position custom properties (desktop fixed + section-absolute states).
      this.style.setProperty('--td-stats-media-left', `${endLeft.toFixed(2)}px`);
      this.style.setProperty('--td-stats-media-screen-left', `${(containerRect.left + endLeft).toFixed(2)}px`);
      this.style.setProperty('--td-stats-media-bottom', `${endBottom.toFixed(2)}px`);
    }

    startRenderLoop() {
      if (this.renderFrame) {
        return;
      }

      // Fresh tracking session — don't compute dt from a stale timestamp.
      this.lastFrameTime = null;
      this.renderFrame = requestAnimationFrame(this.renderMediaFrame);
    }

    stopRenderLoop() {
      if (this.renderFrame) {
        cancelAnimationFrame(this.renderFrame);
        this.renderFrame = null;
      }
    }

    renderMediaFrame(timestamp) {
      if (!this.isTrackingMedia) {
        this.renderFrame = null;
        return;
      }

      const rect = this.getBoundingClientRect();
      const target = this.computeMediaTransform(this.getMediaProgress(rect), rect);

      // Exponential decay smoothing (frame-rate independent).
      const lastFrameTime = this.lastFrameTime ?? timestamp;
      const dt = Math.max(timestamp - lastFrameTime, 0);
      this.lastFrameTime = timestamp;
      const decay = Math.min(1 - Math.exp(-dt / TDStatsHero.scrollSmoothingTau), 1);

      this.currentTransform = this.currentTransform
        ? {
            x: this.lerpTowards(this.currentTransform.x, target.x, decay),
            y: this.lerpTowards(this.currentTransform.y, target.y, decay),
            rotate: this.lerpTowards(this.currentTransform.rotate, target.rotate, decay),
            pinned: target.pinned,
          }
        : target;

      this.setMediaTransform(this.currentTransform);
      this.renderFrame = requestAnimationFrame(this.renderMediaFrame);
    }

    // Closes `decay` of remaining distance; snaps below 0.005.
    lerpTowards(current, target, decay) {
      const delta = target - current;
      return Math.abs(delta) < 0.005 ? target : current + delta * decay;
    }

    // One-off sync outside render loop; no-op when CSS scroll timeline is active.
    renderOnce() {
      if (this.prefersCssTimeline()) {
        return;
      }

      const rect = this.getBoundingClientRect();
      this.currentTransform = this.computeMediaTransform(this.getMediaProgress(rect), rect);
      this.setMediaTransform(this.currentTransform);
    }

    getMediaProgress(rect) {
      const sectionRect = rect || this.getBoundingClientRect();
      const viewportHeight = this.viewportHeight;
      // Progress 0 until section top reaches viewport bottom.
      const start = viewportHeight;
      const end = this.desktopQuery.matches ? viewportHeight * 0.82 : viewportHeight * 0.86;
      const travel = sectionRect.height + (start - end);

      if (travel <= 0) {
        return 1;
      }

      return this.clamp((start - sectionRect.top) / travel, 0, 1);
    }

    computeMediaTransform(progress, rect) {
      if (!this.motionMetrics) {
        this.updateMotionMetrics();
      }

      if (!this.motionMetrics) {
        return { x: 0, y: 0, rotate: 0 };
      }

      const clampedProgress = this.clamp(progress, 0, 1);
      const inverse = 1 - clampedProgress;

      if (this.motionMetrics.mode === 'mobile') {
        // Hockey-stick: sweep + straighten in first 35%; keep in sync with CSS keyframes.
        const sweepInverse = 1 - this.clamp(clampedProgress / 0.35, 0, 1);
        const x = this.motionMetrics.startX * Math.pow(sweepInverse, 1.6);
        const y = this.motionMetrics.startY * Math.pow(inverse, 1.12);
        const rotate = this.motionMetrics.startRotate * Math.pow(sweepInverse, 0.6);
        return { x, y, rotate, pinned: false };
      }

      // Desktop hockey-stick: x/rotate decay in sweepWindow; y arc settles onto pin line.
      const sweepProgress = this.clamp(clampedProgress / this.motionMetrics.sweepWindow, 0, 1);
      const sweepInverse = 1 - sweepProgress;
      const x = this.motionMetrics.startX * Math.pow(sweepInverse, 1.6);
      const rotate = this.motionMetrics.startRotate * Math.pow(sweepInverse, 0.6);

      const sectionRect = rect || this.getBoundingClientRect();

      // Fixed pin avoids absolute + counter-translate scroll mismatch at handoff.
      const pinnedScreenBottom = this.viewportHeight * (1 - this.motionMetrics.pinMargin);
      const naturalScreenBottom = sectionRect.top + sectionRect.height - this.motionMetrics.endBottom;
      const arc = this.motionMetrics.arcLift * 4 * sweepProgress * sweepInverse;
      const y = -arc;
      const pinned = naturalScreenBottom > pinnedScreenBottom;

      return { x, y, rotate, pinned };
    }

    setMediaTransform({ x, y, rotate, pinned = false }) {
      this.media.classList.toggle(TDStatsHero.classes.mediaPinned, pinned);
      this.media.style.setProperty('--td-stats-media-x', `${x.toFixed(2)}px`);
      this.media.style.setProperty('--td-stats-media-y', `${y.toFixed(2)}px`);
      this.media.style.setProperty('--td-stats-media-rotate', `${rotate.toFixed(2)}deg`);
    }

    getIntersectionRoot() {
      if (this.squeezeQuery.matches) {
        return document.querySelector('.page-wrapper') || null;
      }

      return null;
    }

    formatCounterValue(value, decimals) {
      if (decimals > 0) {
        return Number(value.toFixed(decimals)).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      }

      return Math.round(value).toLocaleString();
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  customElements.define('td-stats-hero', TDStatsHero);
}
